/**
 * Format helpers. All monetary values are paise (integers) at the data layer
 * per MIGRATION_CONTRACT.md §9.
 */
/**
 * Format a paise amount as rupees.
 *
 * Accepts `number`, `bigint` and numeric `string`. The revamped API sends
 * `bigint` paise, which serialise to JSON as decimal *strings* — those used to
 * fall through `Number.isNaN(paise)` and render as '—' (defect noted in the
 * revamp brief against `format.ts:5-11`). Behaviour for `number` inputs is
 * unchanged, including the half-up rounding of fractional paise.
 *
 * Anything that is not a finite, integral-paise value renders as '—' rather
 * than as a wrong number.
 */
export function formatINR(
  paise: number | bigint | string | null | undefined,
  opts: { withSymbol?: boolean } = {},
): string {
  const rupees = paiseToRupees(paise)
  if (rupees == null) return '—'
  const s = rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return opts.withSymbol === false ? s : `₹${s}`
}

/**
 * Paise -> rupees as a JS number, or null when the input is not a usable
 * amount. Exported so callers that need the numeric value (charts, sums)
 * share one coercion rule with the formatter.
 *
 * bigint and string inputs go through BigInt arithmetic so a value beyond
 * Number.MAX_SAFE_INTEGER is detected rather than silently mangled.
 */
export function paiseToRupees(paise: number | bigint | string | null | undefined): number | null {
  if (paise == null) return null

  if (typeof paise === 'number') {
    if (!Number.isFinite(paise)) return null
    return Math.round(paise) / 100
  }

  let big: bigint
  if (typeof paise === 'bigint') {
    big = paise
  } else {
    const trimmed = paise.trim()
    // Integer paise only — a decimal string is not a paise amount.
    if (!/^[+-]?\d+$/.test(trimmed)) return null
    try {
      big = BigInt(trimmed)
    } catch {
      return null
    }
  }

  // Split before converting so large values keep full rupee precision.
  const negative = big < 0n
  const abs = negative ? -big : big
  const whole = abs / 100n
  const frac = Number(abs % 100n)

  if (whole > BigInt(Number.MAX_SAFE_INTEGER)) return null

  const value = Number(whole) + frac / 100
  return negative ? -value : value
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/** Mask Aadhaar/PAN/bank for list views. */
export function maskPii(value: string | null | undefined, visible = 4): string {
  if (!value) return '—'
  if (value.length <= visible) return value
  return 'X'.repeat(value.length - visible) + value.slice(-visible)
}
